import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '@/lib/api/client';
import type { Tier1Role } from '@/lib/auth/rbac';

interface WidgetCatalogEntry {
  key: string;
  defaultTitle: string;
  description: string;
}

interface LayoutWidget {
  key: string;
  title: string;
  config?: Record<string, unknown> | null;
}

interface DashboardLayoutResponse {
  role: string;
  widgets: LayoutWidget[];
}

const ROLES: Tier1Role[] = [
  'c_level',
  'it_admin',
  'central_ops',
  'hr_ops',
  'district_manager',
  'regional_manager',
  'store_manager',
  'vendor',
];

export function AdminDashboardLayouts() {
  const [role, setRole] = useState<Tier1Role>('store_manager');
  const [catalog, setCatalog] = useState<WidgetCatalogEntry[]>([]);
  const [layout, setLayout] = useState<LayoutWidget[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ widgets: WidgetCatalogEntry[] }>('/widgets')
      .then((c) => setCatalog(c.widgets))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  useEffect(() => {
    setSavedAt(null);
    apiFetch<DashboardLayoutResponse>(`/dashboard-layouts/${role}`)
      .then((l) => setLayout(l.widgets))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [role]);

  const catalogByKey = useMemo(() => {
    const m = new Map<string, WidgetCatalogEntry>();
    for (const w of catalog) m.set(w.key, w);
    return m;
  }, [catalog]);

  const addable = catalog.filter((w) => !layout.some((l) => l.key === w.key));

  function onDragStart(idx: number) {
    setDragIndex(idx);
  }
  function onDragOver(idx: number, e: React.DragEvent) {
    e.preventDefault();
    setOverIndex(idx);
  }
  function onDrop(idx: number) {
    if (dragIndex === null || dragIndex === idx) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    setLayout((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      if (moved !== undefined) {
        next.splice(idx, 0, moved);
      }
      return next;
    });
    setDragIndex(null);
    setOverIndex(null);
  }

  function remove(idx: number) {
    setLayout((prev) => prev.filter((_, i) => i !== idx));
  }
  function add(key: string) {
    const def = catalogByKey.get(key);
    if (!def) return;
    setLayout((prev) => [...prev, { key, title: def.defaultTitle }]);
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await apiFetch(`/dashboard-layouts/${role}`, {
        method: 'PUT',
        body: JSON.stringify({ widgets: layout }),
      });
      setSavedAt(new Date().toISOString());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-semibold mb-1">Dashboard layouts</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Drag widgets to reorder. Restricted to <code>TMKN-Qargo-Admins</code>.
      </p>

      <div className="flex items-center gap-3 mb-6">
        <label htmlFor="role-select" className="text-sm">
          Role:
        </label>
        <select
          id="role-select"
          value={role}
          onChange={(e) => setRole(e.target.value as Tier1Role)}
          className="border border-border rounded-md px-2 py-1 text-sm"
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          className="ml-auto px-4 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save layout'}
        </button>
      </div>

      {error ? <p className="text-sm text-destructive mb-4">{error}</p> : null}
      {savedAt ? <p className="text-sm text-muted-foreground mb-4">Saved at {savedAt}</p> : null}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <section>
          <h2 className="text-sm font-medium mb-2">Layout (drag to reorder)</h2>
          <ul className="space-y-2">
            {layout.map((w, idx) => (
              <li
                key={`${w.key}-${idx}`}
                draggable
                onDragStart={() => onDragStart(idx)}
                onDragOver={(e) => onDragOver(idx, e)}
                onDrop={() => onDrop(idx)}
                onDragEnd={() => {
                  setDragIndex(null);
                  setOverIndex(null);
                }}
                className={`flex items-center justify-between border rounded-md px-3 py-2 bg-card cursor-move ${
                  overIndex === idx && dragIndex !== null && dragIndex !== idx
                    ? 'border-primary'
                    : 'border-border'
                }`}
              >
                <span className="text-sm">
                  <span className="text-muted-foreground mr-2">{idx + 1}.</span>
                  {w.title}
                  <span className="ml-2 text-xs text-muted-foreground">({w.key})</span>
                </span>
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  className="text-xs text-destructive underline underline-offset-4"
                >
                  Remove
                </button>
              </li>
            ))}
            {layout.length === 0 ? (
              <li className="text-sm text-muted-foreground italic">No widgets yet.</li>
            ) : null}
          </ul>
        </section>

        <section>
          <h2 className="text-sm font-medium mb-2">Catalog (click to add)</h2>
          <ul className="space-y-2">
            {addable.map((w) => (
              <li
                key={w.key}
                className="flex items-center justify-between border border-border rounded-md px-3 py-2"
              >
                <div>
                  <div className="text-sm">{w.defaultTitle}</div>
                  <div className="text-xs text-muted-foreground">{w.description}</div>
                </div>
                <button
                  type="button"
                  onClick={() => add(w.key)}
                  className="text-xs underline underline-offset-4"
                >
                  Add
                </button>
              </li>
            ))}
            {addable.length === 0 ? (
              <li className="text-sm text-muted-foreground italic">All widgets in layout.</li>
            ) : null}
          </ul>
        </section>
      </div>
    </div>
  );
}
